import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import api from "../utils/api";
import { 
  Package, 
  Plus, 
  Trash2, 
  Edit2, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  History,
  Info,
  DollarSign
} from "lucide-react";

const InventoryPage = () => {
  const [ingredients, setIngredients] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("list"); // "list" or "ledger"
  const [ledgerPage, setLedgerPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);

  // Forms state
  const [addForm, setAddForm] = useState({
    name: "",
    unit: "g",
    currentStock: 0,
    costPerUnit: 0,
    alertThreshold: 0
  });

  const [editForm, setEditForm] = useState({
    name: "",
    unit: "g",
    alertThreshold: 0
  });

  const [restockForm, setRestockForm] = useState({
    quantity: "",
    costPerUnit: ""
  });

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await api.get("inventory");
      if (res.data && res.data.success) {
        setIngredients(res.data.data);
      }
    } catch (error) {
      console.error("Failed to load inventory:", error);
      toast.error("Failed to load inventory ingredients");
    } finally {
      setLoading(false);
    }
  };

  const fetchLedger = async (page = 1) => {
    try {
      setLedgerLoading(true);
      const res = await api.get(`inventory/transactions?page=${page}&limit=15`);
      if (res.data && res.data.success) {
        setTransactions(res.data.data);
        setLedgerPage(res.data.currentPage);
        setTotalPages(res.data.totalPages);
      }
    } catch (error) {
      console.error("Failed to load ledger logs:", error);
      toast.error("Failed to load transaction ledger logs");
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  useEffect(() => {
    if (activeTab === "ledger") {
      fetchLedger(ledgerPage);
    }
  }, [activeTab, ledgerPage]);

  // Form handlers
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) {
      toast.error("Ingredient name is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("inventory", addForm);
      if (res.data && res.data.success) {
        toast.success("Ingredient added successfully!");
        fetchInventory();
        setShowAddModal(false);
        setAddForm({ name: "", unit: "g", currentStock: 0, costPerUnit: 0, alertThreshold: 0 });
      }
    } catch (error) {
      console.error("Add ingredient error:", error);
      toast.error(error.response?.data?.message || "Failed to add ingredient");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (item) => {
    setSelectedIngredient(item);
    setEditForm({
      name: item.name,
      unit: item.unit,
      alertThreshold: item.alertThreshold
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      toast.error("Ingredient name is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.put(`inventory/${selectedIngredient._id}`, editForm);
      if (res.data && res.data.success) {
        toast.success("Ingredient updated successfully!");
        fetchInventory();
        setShowEditModal(false);
      }
    } catch (error) {
      console.error("Edit ingredient error:", error);
      toast.error(error.response?.data?.message || "Failed to update ingredient");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestockClick = (item) => {
    setSelectedIngredient(item);
    setRestockForm({ quantity: "", costPerUnit: item.costPerUnit || "" });
    setShowRestockModal(true);
  };

  const handleRestockSubmit = async (e) => {
    e.preventDefault();
    if (!restockForm.quantity || Number(restockForm.quantity) <= 0) {
      toast.error("Please enter a valid positive quantity");
      return;
    }
    if (restockForm.costPerUnit === "" || Number(restockForm.costPerUnit) < 0) {
      toast.error("Please enter a valid cost per unit");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`inventory/${selectedIngredient._id}/add-stock`, {
        quantity: Number(restockForm.quantity),
        costPerUnit: Number(restockForm.costPerUnit)
      });
      if (res.data && res.data.success) {
        toast.success("Stock updated successfully!");
        fetchInventory();
        setShowRestockModal(false);
      }
    } catch (error) {
      console.error("Restock error:", error);
      toast.error("Failed to add stock");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (item) => {
    Swal.fire({
      title: `Delete ${item.name}?`,
      text: "This will permanently remove the ingredient from inventory.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await api.delete(`inventory/${item._id}`);
          if (res.data && res.data.success) {
            toast.success("Ingredient deleted successfully");
            fetchInventory();
          }
        } catch (error) {
          console.error("Delete error:", error);
          toast.error(error.response?.data?.message || "Failed to delete ingredient");
        }
      }
    });
  };

  return (
    <div className="container mx-auto px-4 mt-20 pb-10">
      <ToastContainer />

      {/* Header Panel */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-800 rounded-3xl p-6 md:p-8 text-white shadow-xl mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
              <Package className="w-8 h-8 text-teal-200" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold font-serif">Inventory & Ingredients</h1>
              <p className="text-teal-100 text-sm mt-0.5">Manage raw materials, track stock valuations, and configure alerts.</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 bg-white text-teal-800 font-bold px-6 py-3 rounded-2xl shadow hover:bg-teal-50 transition-all duration-200"
          >
            <Plus className="w-5 h-5" />
            <span>Add Ingredient</span>
          </button>
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab("list")}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all px-2 ${
            activeTab === "list"
              ? "border-teal-600 text-teal-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <span className="flex items-center gap-2">
            <Package className="w-4 h-4" /> Ingredient List
          </span>
        </button>
        <button
          onClick={() => setActiveTab("ledger")}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all px-2 ${
            activeTab === "ledger"
              ? "border-teal-600 text-teal-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <span className="flex items-center gap-2">
            <History className="w-4 h-4" /> Stock Ledger Logs
          </span>
        </button>
      </div>

      {/* Tab: Ingredient List */}
      {activeTab === "list" && (
        <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 font-medium">Loading inventory data...</p>
            </div>
          ) : ingredients.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-semibold">No ingredients found</p>
              <p className="text-sm mt-1">Start adding ingredients to build recipes and track margins.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ingredients.map((item) => {
                const isLowStock = item.currentStock <= item.alertThreshold;
                const isOutOfStock = item.currentStock <= 0;

                return (
                  <div 
                    key={item._id}
                    className={`relative rounded-2xl p-5 border transition-all duration-200 ${
                      isOutOfStock 
                        ? "bg-red-50/50 border-red-200" 
                        : isLowStock 
                          ? "bg-amber-50/50 border-amber-200" 
                          : "bg-white border-gray-100 shadow-sm hover:shadow-md"
                    }`}
                  >
                    {/* Top row */}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-gray-800 text-lg">{item.name}</h3>
                        <p className="text-xs text-gray-400">Avg Cost: ₹{item.costPerUnit.toFixed(2)} / {item.unit}</p>
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => handleEditClick(item)}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition"
                          title="Edit ingredient"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Delete ingredient"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Stock level indicators */}
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-gray-500">Stock Status:</span>
                        <span className={`font-bold ${
                          isOutOfStock 
                            ? "text-red-600" 
                            : isLowStock 
                              ? "text-amber-600" 
                              : "text-teal-600"
                        }`}>
                          {item.currentStock.toLocaleString()} {item.unit}
                        </span>
                      </div>
                      
                      {/* stock progress bar */}
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-300 ${
                            isOutOfStock 
                              ? "w-0" 
                              : isLowStock 
                                ? "bg-amber-500 w-[25%]" 
                                : "bg-teal-500 w-full"
                          }`}
                        />
                      </div>
                    </div>

                    {/* Alert boxes */}
                    {isOutOfStock ? (
                      <div className="flex items-center gap-2 bg-red-100 text-red-800 px-3 py-1.5 rounded-xl text-xs font-semibold mb-4">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>Critical: Out of Stock!</span>
                      </div>
                    ) : isLowStock ? (
                      <div className="flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-xl text-xs font-semibold mb-4">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>Low Stock! Threshold: {item.alertThreshold} {item.unit}</span>
                      </div>
                    ) : (
                      <div className="h-[34px] mb-4" /> /* spacer to match height */
                    )}

                    {/* Footer actions */}
                    <div className="border-t border-gray-100 pt-3 flex justify-between items-center">
                      <div className="text-xs text-gray-400">
                        Total Value: <span className="font-bold text-gray-700">₹{(item.currentStock * item.costPerUnit).toFixed(2)}</span>
                      </div>
                      <button
                        onClick={() => handleRestockClick(item)}
                        className="bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-bold px-3 py-1.5 rounded-xl transition"
                      >
                        🛒 Restock Item
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab: Stock Ledger Logs */}
      {activeTab === "ledger" && (
        <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center gap-2 mb-6 text-gray-700">
            <History className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-bold">Stock Ledger Movements</h2>
          </div>

          {ledgerLoading && transactions.length === 0 ? (
            <div className="text-center py-20 text-gray-500">Loading ledger logs...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-20 text-gray-400">No transactions logged yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ingredient</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Movement</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Cost Per Unit</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Reason</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {log.ingredientId?.name || "Deleted Ingredient"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          log.type === "IN" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        }`}>
                          {log.type === "IN" ? (
                            <span className="flex items-center gap-1">
                              <TrendingUp className="w-3.5 h-3.5" /> +{log.quantity} {log.ingredientId?.unit}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <TrendingDown className="w-3.5 h-3.5" /> -{log.quantity} {log.ingredientId?.unit}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        ₹{log.costPerUnit > 0 ? log.costPerUnit.toFixed(2) : "0.00"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                        {log.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 border-t border-gray-100 pt-4">
                  <span className="text-sm text-gray-500">Page {ledgerPage} of {totalPages}</span>
                  <div className="flex gap-2">
                    <button
                      disabled={ledgerPage === 1}
                      onClick={() => setLedgerPage(ledgerPage - 1)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      disabled={ledgerPage === totalPages}
                      onClick={() => setLedgerPage(ledgerPage + 1)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal: Add Ingredient */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-gray-100">
            <h2 className="text-xl font-bold font-serif mb-4 text-teal-800">Add New Ingredient</h2>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-700">Ingredient Name *</label>
                <input
                  type="text"
                  placeholder="E.g. Cheese, Flour, Milk"
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700">Unit *</label>
                  <select
                    value={addForm.unit}
                    onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  >
                    <option value="g">grams (g)</option>
                    <option value="ml">milliliters (ml)</option>
                    <option value="pcs">pieces (pcs)</option>
                    <option value="kg">kilograms (kg)</option>
                    <option value="liters">liters (l)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700">Alert Threshold</label>
                  <input
                    type="number"
                    value={addForm.alertThreshold}
                    onChange={(e) => setAddForm({ ...addForm, alertThreshold: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-500">Initial Stock</label>
                  <input
                    type="number"
                    value={addForm.currentStock}
                    onChange={(e) => setAddForm({ ...addForm, currentStock: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-500">Initial Cost / Unit (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={addForm.costPerUnit}
                    onChange={(e) => setAddForm({ ...addForm, costPerUnit: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-gray-700 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold shadow transition text-sm disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Add Ingredient"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit Ingredient */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-gray-100">
            <h2 className="text-xl font-bold font-serif mb-4 text-teal-800">Edit Ingredient</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-700">Ingredient Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700">Unit *</label>
                  <select
                    value={editForm.unit}
                    onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  >
                    <option value="g">grams (g)</option>
                    <option value="ml">milliliters (ml)</option>
                    <option value="pcs">pieces (pcs)</option>
                    <option value="kg">kilograms (kg)</option>
                    <option value="liters">liters (l)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700">Alert Threshold</label>
                  <input
                    type="number"
                    value={editForm.alertThreshold}
                    onChange={(e) => setEditForm({ ...editForm, alertThreshold: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-gray-700 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold shadow transition text-sm disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Restock Ingredient */}
      {showRestockModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl border border-gray-100">
            <div className="flex items-center gap-2 mb-3 text-teal-800">
              <DollarSign className="w-5 h-5" />
              <h2 className="text-xl font-bold font-serif">Restock: {selectedIngredient?.name}</h2>
            </div>
            
            <div className="bg-teal-50 p-3 rounded-2xl flex items-center gap-2.5 text-xs text-teal-800 mb-4 border border-teal-100">
              <Info className="w-4 h-4 shrink-0 text-teal-600" />
              <span>Restocking automatically recalculates average weighted ingredient unit costs.</span>
            </div>

            <form onSubmit={handleRestockSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-700">Restock Quantity ({selectedIngredient?.unit})</label>
                <input
                  type="number"
                  placeholder="Quantity to add"
                  value={restockForm.quantity}
                  onChange={(e) => setRestockForm({ ...restockForm, quantity: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-700">Cost paid per Unit (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Price paid per unit"
                  value={restockForm.costPerUnit}
                  onChange={(e) => setRestockForm({ ...restockForm, costPerUnit: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  required
                />
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowRestockModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-gray-700 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold shadow transition text-sm disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Log Restock"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
